import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import Button from '~/components/buttons/NewButton';
import TextController from '~/components/controllers/TextController/TextController';
import AlertMessage from '~/components/data-display/AlertMessage';
import { HStack, VStack } from '~/components/layout/Stack';
import Modal from '~/components/modals/Modal/Modal';
import ModalActions from '~/components/modals/Modal/ModalActions';
import ModalContent from '~/components/modals/Modal/ModalContent';
import ModalTitle from '~/components/modals/Modal/ModalTitle';
import Text from '~/components/typographies/Text';
import { snack } from '~/hooks/ToastProvider';
import { useErrorMap } from '~/hooks/forms/useErrorMap';
import { useAnalytics } from '~/hooks/useAnalytics';
import { ModalProps } from '~/modals/Modal';
import { AnalyticsEvents } from '~/services/analytics';
import { useCreateRegistrationTemplate, useRegistrationTemplates } from '~/services/bookkeeping/registration-templates';
import { RegistrationTemplate, RegistrationTemplateData } from '~/types/registration-template';
import styles from './CreateRegistrationTemplateModal.module.scss';

const CreateRegistrationTemplateSchema = z.object({
  name: z.string().min(1),
});

type CreateRegistrationTemplateFormInput = z.input<typeof CreateRegistrationTemplateSchema>;
type CreateRegistrationTemplateFormOutput = z.output<typeof CreateRegistrationTemplateSchema>;

interface Props extends ModalProps {
  templateData: RegistrationTemplateData;
  onCreated?: (template: RegistrationTemplate) => void;
}

const CreateRegistrationTemplateModal = ({ closeModal, templateData, onCreated }: Props) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'registrations.create.manual_registration.templates' });
  const { mutateAsync, isPending } = useCreateRegistrationTemplate();
  const { data: existingTemplates } = useRegistrationTemplates();
  const { track } = useAnalytics();

  const errorMap = useErrorMap([{ issue: 'template_name_already_exists', message: t('template_name_already_exists') }]);

  const schema = useMemo(() => {
    const existingNames = new Set((existingTemplates ?? []).map((tpl) => tpl.name.trim().toLowerCase()));
    return CreateRegistrationTemplateSchema.refine(({ name }) => !existingNames.has(name.trim().toLowerCase()), {
      params: { issue: 'template_name_already_exists' },
      path: ['name'],
    });
  }, [existingTemplates]);

  const {
    control,
    handleSubmit,
    formState: { errors, submitCount },
  } = useForm<CreateRegistrationTemplateFormInput, null, CreateRegistrationTemplateFormOutput>({
    defaultValues: { name: '' },
    resolver: zodResolver(schema, { error: errorMap }),
  });

  const showValidationAlert = submitCount > 0 && Object.keys(errors).length > 0;

  const onSubmit = async ({ name }: CreateRegistrationTemplateFormOutput) => {
    try {
      const created = await mutateAsync({ name, ...templateData });
      track(AnalyticsEvents.REGISTRATION_TEMPLATE_CREATED, {
        templateType: templateData.templateType,
        name,
      });
      onCreated?.(created);
      snack.success(t('template_saved'));
      closeModal();
    } catch {
      snack.error(t('template_save_error'));
    }
  };

  return (
    <Modal action="confirm" isOpen maxWidth={480} fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack gap={3}>
          <ModalTitle onCloseClick={closeModal}>
            <Text variant="subtitle2">{t('add_template')}</Text>
          </ModalTitle>

          <ModalContent className={styles.content}>
            <VStack gap={3}>
              <Text variant="body2" weight="regular">
                {t('template_use_description')}
              </Text>
              <TextController
                control={control}
                name="name"
                autoFocus
                label={t('template_name')}
                placeholder={t('template_name_placeholder')}
                required
                forceIgnore1Password
              />

              {showValidationAlert && (
                <AlertMessage box fullWidth type="error">
                  {t('error_validation_form')}
                </AlertMessage>
              )}
            </VStack>
          </ModalContent>

          <ModalActions>
            <VStack gap={2}>
              <HStack gap={2} justify="end">
                <Button variant="outlined" disabled={isPending} onClick={() => closeModal()}>
                  {t('cancel')}
                </Button>
                <Button type="submit" loading={isPending}>
                  {t('save')}
                </Button>
              </HStack>
            </VStack>
          </ModalActions>
        </VStack>
      </form>
    </Modal>
  );
};

export default CreateRegistrationTemplateModal;
